import {Component, computed, inject, OnDestroy, OnInit, signal, viewChildren} from '@angular/core'
import {FormsModule} from '@angular/forms'
import {MatDatepickerModule} from '@angular/material/datepicker'
import {MatInputModule} from '@angular/material/input'
import {TKycEmploymentType, TKycOccupationType} from '@sparbanken-syd/borgo-types'

import {GrossSalaryComponent} from '../../../form/00-pure/113-gross-salary/gross-salary.component'
import {FormOfEmploymentComponent} from '../../../form/00-pure/77-form-of-employment/form-of-employment.component'
import {OccupationComponent} from '../../../form/00-pure/98-occupation/occupation.component'
import {EmployerComponent} from '../../../form/00-pure/99-employer/employer.component'
import {EmploymentKycTypeMap, OccupationTypeMap} from '@sparbanken-syd/borgo-helpers'
import {allowedFormOfEmploymentFields, allowedOccupationFields} from '../../../types'
import {BaseComponent} from '../../../form/00-pure/base.component'
import {DateSelectComponent} from '../../../form/00-pure/5-date-select/date-select.component'
import {DateTime} from 'luxon'
import {KycService} from '../../../services/kyc.service'
import {DateRangeSelectComponent} from '../../../form/00-pure/5a-date-range-select/date-range-select.component'

@Component({
  selector: 'spb-employment',
  imports: [
    FormsModule,
    MatDatepickerModule,
    MatInputModule,
    FormOfEmploymentComponent,
    EmployerComponent,
    GrossSalaryComponent,
    OccupationComponent,
    DateSelectComponent,
    DateRangeSelectComponent
  ],
  templateUrl: './employment.component.html',
  styleUrl: './employment.component.scss'
})
export class EmploymentComponent implements OnInit, OnDestroy {

  public startDate = signal<DateTime | null>(DateTime.now())
  public endDate = signal<DateTime | null>(DateTime.now())

  public range = signal<[DateTime, DateTime]>([this.startDate()!, this.endDate()!])

  //
  public salary = signal<number>(0)
  public employer = signal<string | null>(null)

  // Can and should be used
  public occupation = signal<TKycOccupationType | null>(null)
  public employmentForm = signal<TKycEmploymentType | null>(null)

  /**
   * Sort out a list of available occupations, occupation is like Anställd
   * or Arbetslös
   */
  public occupations = computed(() => {
    const allowed: [string, string][] = [...OccupationTypeMap.entries()].map(([k, v]): [string, string] => {
      // for some reason this has to be renamed.
      if (k === 'EMPLOYEE') {
        return [k, 'Anställd']
      }
      return [k, v.name]
    }).filter(([k]) => allowedOccupationFields.includes(k as TKycOccupationType))
    return new Map<string, string>(allowed)
  })

  /**
   * Employment type can be a long list, fixed, part_time or care leave
   * etc. In this application we only allow two, fixed term and temporarily
   */
  public employmentForms = computed(() => {
    const allowed =
      [...EmploymentKycTypeMap.entries()].map(([k, v]): [string, string] => {
        return [k, v.name]
      }).filter(([k]) => allowedFormOfEmploymentFields.includes(k as TKycEmploymentType))
    return new Map<string, string>(allowed)
  })


  public readonly children = viewChildren(BaseComponent)

  public valid = computed(() => {
    return this.children().every(child => child.valid())
  })

  private readonly service = inject(KycService)

  public ngOnInit() {
    const e = this.service.kycModel.employment
    this.employmentForm.set(e.employment)
    this.occupation.set(e.occupation)
    this.startDate.set(e.startDate)
    this.endDate.set(e.endDate)
    this.salary.set(e.salary)
    this.employer.set(e.employer)
  }

  public ngOnDestroy(): void {
    // The recipient will make sure to unset invalid values.
    this.service.kycModel.employment = {
      employer: this.employer(),
      salary: this.salary(),
      occupation: this.occupation(),
      employment: this.employmentForm(),
      startDate: this.startDate(),
      endDate: this.endDate()
    }
  }
}
